import { Link, useLocation } from "react-router-dom";
import digavouchLogo from "@/assets/digavouch-logo.svg";
import { useOptionalAuth } from "@/contexts/AuthContext";
import { IS_PRODUCTION } from "@/lib/env";

/**
 * Shared DigaVouch top navigation, mirroring digavouch.com.
 * Used across all live-mode pages via AppShell so login, applicant flow,
 * voucher flow, and the dashboard share the same brand chrome.
 */
export function BrandHeader({ minimal = false }: { minimal?: boolean }) {
  return (
    <header className="relative z-10 w-full">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a
          href="https://digavouch.com"
          className="flex items-center gap-3 group transition-transform hover:scale-[1.02]"
        >
          <img src={digavouchLogo} alt="DigaVouch" className="h-11 w-11" />
          <div className="leading-tight">
            <div className="text-[17px] font-bold text-slate-900 tracking-tight">DigaVouch</div>
            <div className="text-[10px] font-semibold tracking-[0.18em] text-slate-500 uppercase">
              Identity Systems
            </div>
          </div>
        </a>

        {!minimal && (
          <>
            <nav className="hidden md:flex items-center gap-9 text-sm font-medium text-slate-600">
              <a href="https://digavouch.com/#solutions" className="hover:text-slate-900 transition-colors">Solutions</a>
              <a href="https://digavouch.com/#how-it-works-diagram" className="hover:text-slate-900 transition-colors">How It Works</a>
              <a href="https://digavouch.com/#faqs" className="hover:text-slate-900 transition-colors">FAQs</a>
              <a href="https://digavouch.com/privacy" className="hover:text-slate-900 transition-colors">Privacy</a>
            </nav>

            <a
              href="https://digavouch.com/request-demo"
              className="hidden sm:inline-flex items-center justify-center h-10 px-5 rounded-full text-sm font-semibold text-white shadow-sm hover:shadow-md hover:-translate-y-px transition-all"
              style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}
            >
              Request Demo
            </a>
          </>
        )}
      </div>
    </header>
  );
}

/**
 * Routes where the demo-mode footer links should be surfaced. We keep them
 * out of the public applicant/voucher flows so real applicants never see
 * operator-only navigation.
 */
const OPERATOR_PATH_PREFIXES = ["/dashboard", "/recruiting", "/applicant/", "/demo", "/demo-new"];

function isOperatorPath(pathname: string): boolean {
  return OPERATOR_PATH_PREFIXES.some((p) =>
    p.endsWith("/") ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function BrandFooter({ compactArticleFlow = false }: { compactArticleFlow?: boolean }) {
  const { user } = useOptionalAuth();
  const { pathname } = useLocation();
  // Hide demo links on production (VITE_APP_ENV=production). UAT + staging
  // still surface them for internal demo-ing. Pair with App.tsx where the
  // /demo/* routes themselves are gated so a direct URL also 404s in prod.
  const showDemoLinks = !IS_PRODUCTION && !!user && isOperatorPath(pathname);

  if (compactArticleFlow) {
    return (
      <footer className="article-flow-footer relative z-10 w-full">
        <div className="article-flow-footer-inner">
          <span>Powered by DigaVouch</span>
          <nav aria-label="Legal">
            <a href="https://digavouch.com/privacy">Privacy</a>
            <a href="/cookie-policy">Cookies</a>
          </nav>
        </div>
      </footer>
    );
  }

  return (
    <footer className="relative z-10 w-full border-t border-slate-200/60 bg-white/40 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 min-h-14 py-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-2 text-xs text-slate-500">
        <span>© {new Date().getFullYear()} DigaVouch · Identity Systems</span>
        <div className="flex items-center gap-5 flex-wrap">
          {showDemoLinks && (
            <>
              {/* One link to the /demo index rather than three to individual
                  routes — the hub lists those, plus any added later, so this
                  footer stops growing a link per demo surface. */}
              <Link to="/demo" className="hover:text-slate-900 transition-colors">
                Demo
              </Link>
              <span className="h-3 w-px bg-slate-300" aria-hidden />
            </>
          )}
          <a href="https://digavouch.com/privacy" className="hover:text-slate-900 transition-colors">Privacy</a>
          <a href="/cookie-policy" className="hover:text-slate-900 transition-colors">Cookies</a>
        </div>
      </div>
    </footer>
  );
}
